import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Gift, Plus, LogOut, BarChart3, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { PartnerAnalyticsCard } from "@/components/PartnerAnalyticsCard";

const STATUS_TONE = {
  pending_admin_review: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  active: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  rejected: "border-rose-500/40 text-rose-300 bg-rose-500/10",
};

/**
 * Perk Partner Portal — dashboard for local businesses to manage their perks.
 */
export default function PartnerPortalPage() {
  const { user, logout } = useAuth();
  const [partner, setPartner] = useState(null);
  const [perks, setPerks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [claims, setClaims] = useState({});
  const [busy, setBusy] = useState(false);
  const empty = {
    title: "", description: "",
    discount_type: "percent", discount_value: 10,
    promo_code: "", redeem_url: "", redeem_instructions: "",
    expires_at: "", max_claims: null,
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    try {
      const [{ data: p }, { data: mine }] = await Promise.all([
        api.get("/perk-partners/me"),
        api.get("/perk-partners/me/perks"),
      ]);
      setPartner(p.partner);
      setPerks(mine.items || []);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load partner data"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(empty);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        discount_value: form.discount_value === "" ? null : Number(form.discount_value),
        max_claims: form.max_claims || null,
      };
      await api.post("/perk-partners/me/perks", payload);
      toast.success("Submitted for admin review");
      setDialogOpen(false);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Submit failed"); }
    setBusy(false);
  };

  const openClaims = async (perk) => {
    setSelected(perk);
    try {
      const { data } = await api.get(`/perk-partners/me/perks/${perk.id}/claims`);
      setClaims({ ...claims, [perk.id]: data });
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load claims"); }
  };

  if (!partner) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Card className="p-6 border-white/10">Loading partner profile…</Card>
      </div>
    );
  }

  const pending = partner.status === "pending";
  const rejected = partner.status === "rejected";

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-zinc-950 to-orange-950/20 text-white">
      <header className="border-b border-white/5 backdrop-blur bg-black/40 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center gap-3 p-4">
          <img src="/prestigious-logo.png" alt="Prestigious Property Management Services" className="h-10 w-10" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.24em] text-zinc-500">Perk Partner Portal</div>
            <div className="text-sm font-semibold truncate">{partner.business_name}</div>
          </div>
          <Badge variant="outline" className={STATUS_TONE[partner.status] || "border-white/10 text-zinc-400"} data-testid="partner-status">
            {partner.status.replaceAll("_", " ")}
          </Badge>
          <Button variant="ghost" size="sm" onClick={logout} className="text-zinc-400 hover:text-white">
            <LogOut className="h-3.5 w-3.5 mr-1" /> Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6">
        {pending && (
          <Card className="p-6 border-amber-500/30 bg-amber-500/[0.03] mb-4" data-testid="partner-pending-banner">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-400" />
              <div>
                <div className="text-sm font-semibold">Awaiting admin approval</div>
                <div className="text-xs text-zinc-400 mt-1">Once your account is verified, you can start publishing perks and viewing claim analytics here.</div>
              </div>
            </div>
          </Card>
        )}

        {rejected && (
          <Card className="p-6 border-rose-500/30 bg-rose-500/[0.03] mb-4" data-testid="partner-rejected-banner">
            <div className="flex items-center gap-3">
              <XCircle className="h-5 w-5 text-rose-400" />
              <div>
                <div className="text-sm font-semibold">Application not approved</div>
                <div className="text-xs text-zinc-400 mt-1">Contact the property manager for details.</div>
              </div>
            </div>
          </Card>
        )}

        {partner.status === "approved" && <PartnerAnalyticsCard />}

        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Your published perks</div>
            <div className="text-2xl font-display font-semibold">{perks.length} perk{perks.length === 1 ? "" : "s"}</div>
          </div>
          <Button onClick={openNew} disabled={!partner || partner.status !== "approved"}
                  className="bg-orange-500 hover:bg-orange-600 rounded-full"
                  data-testid="partner-new-perk">
            <Plus className="h-4 w-4 mr-1" /> Publish perk
          </Button>
        </div>

        {perks.length === 0 ? (
          <Card className="p-10 text-center border-white/10" data-testid="partner-perks-empty">
            <Gift className="h-8 w-8 mx-auto text-zinc-500 mb-2" />
            <div className="text-sm text-zinc-400">No perks published yet. Once you&apos;re approved, publish your first offer to reach every resident in this community.</div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {perks.map((p) => (
              <Card key={p.id} className="p-4 border-white/10 bg-card" data-testid={`partner-perk-${p.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-100 truncate">{p.title}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{p.discount_type} · {p.discount_value || "—"}</div>
                  </div>
                  <Badge variant="outline" className={STATUS_TONE[p.status] || "border-white/10 text-zinc-400"}>
                    {p.status?.replaceAll("_", " ")}
                  </Badge>
                </div>
                {p.description && <div className="text-xs text-zinc-400 line-clamp-2">{p.description}</div>}
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[11px] text-zinc-500">
                    {p.claim_count || 0} claim{p.claim_count === 1 ? "" : "s"}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => openClaims(p)}
                          className="border-white/10 rounded-full"
                          data-testid={`partner-perk-analytics-${p.id}`}>
                    <BarChart3 className="h-3 w-3 mr-1" /> Analytics
                  </Button>
                </div>
                {claims[p.id] && (
                  <div className="mt-2 rounded-md bg-white/[0.02] border border-white/5 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Recent claims</div>
                    <div className="flex flex-wrap gap-1">
                      {claims[p.id].items.slice(0, 12).map((c) => (
                        <div key={c.id} title={new Date(c.created_at).toLocaleString()}
                             className="h-6 w-6 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center text-[9px] text-zinc-300 font-mono">
                          {c.user_initials}
                        </div>
                      ))}
                      {claims[p.id].items.length === 0 && <span className="text-[11px] text-zinc-500 italic">No claims yet.</span>}
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[560px]" data-testid="partner-perk-dialog">
            <DialogHeader>
              <DialogTitle>Publish a perk</DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs">Perks are queued for a quick admin review before residents see them.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <div>
                <Label className="text-xs text-zinc-400">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                       placeholder="20% off morning pastries"
                       className="bg-white/[0.04] border-white/10" data-testid="partner-perk-title" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Description</Label>
                <Textarea rows={2} value={form.description}
                          onChange={(e) => setForm({ ...form, description: e.target.value })}
                          className="bg-white/[0.04] border-white/10 text-sm" data-testid="partner-perk-desc" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-zinc-400">Type</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                    <SelectTrigger className="bg-white/[0.04] border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                      <SelectItem value="percent">percent</SelectItem>
                      <SelectItem value="flat">flat</SelectItem>
                      <SelectItem value="bogo">bogo</SelectItem>
                      <SelectItem value="freebie">freebie</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Value</Label>
                  <Input type="number" value={form.discount_value ?? ""}
                         onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                         className="bg-white/[0.04] border-white/10" data-testid="partner-perk-value" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Promo code</Label>
                  <Input value={form.promo_code}
                         onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
                         className="bg-white/[0.04] border-white/10 font-mono" data-testid="partner-perk-code" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-zinc-400">Expires</Label>
                  <Input type="datetime-local" value={form.expires_at?.slice(0, 16) || ""}
                         onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                         className="bg-white/[0.04] border-white/10" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Max claims</Label>
                  <Input type="number" value={form.max_claims ?? ""}
                         onChange={(e) => setForm({ ...form, max_claims: e.target.value ? Number(e.target.value) : null })}
                         className="bg-white/[0.04] border-white/10" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Redemption instructions</Label>
                <Textarea rows={2} value={form.redeem_instructions}
                          onChange={(e) => setForm({ ...form, redeem_instructions: e.target.value })}
                          className="bg-white/[0.04] border-white/10 text-sm" data-testid="partner-perk-instructions" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy} className="border-white/10">Cancel</Button>
              <Button onClick={save} disabled={busy || !form.title}
                      className="bg-orange-500 hover:bg-orange-600"
                      data-testid="partner-perk-submit">
                {busy ? "Submitting…" : "Submit for review"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
